import ChatVoiceError from "@mapmycustomers/shared/enum/chat/ChatVoiceError";
import ChatVoiceHint from "@mapmycustomers/shared/enum/chat/ChatVoiceHint";

import loggingService from "@app/util/logging";

import { readVoiceTunables } from "./consts";
import createClipRecorder, { ClipRecorder } from "./createClipRecorder";
import createLevelAnalyser, { LevelAnalyser } from "./createLevelAnalyser";
import { debugLogCut, debugRecordLevel, debugResetLevels, VoiceCutReason } from "./debugCutLog";
import isSpeech, { isSilent } from "./isSpeech";

export interface VoiceCapture {
  // Ends the session: stops the tracks and closes the AudioContext. The only place the mic light
  // dies, and the only place the stream is released. Idempotent.
  close(): void;
  getLevel(): number;
  // Stops the current recorder and resolves once its clip has been emitted.
  pause(): Promise<void>;
  // Constructs a new recorder on the same stream. Only valid after pause().
  resume(): void;
  start(): Promise<void>;
  // Ends recording. Resolves with the TOTAL clips emitted this session.
  stop(): Promise<number>;
}

const toVoiceError = (error: unknown): ChatVoiceError => {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return ChatVoiceError.BLOCKED;
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return ChatVoiceError.NO_DEVICE;
  }
  return ChatVoiceError.UNKNOWN;
};

const createVoiceCapture = ({
  audioContext,
  onClip,
  onError,
  onHintChange,
}: {
  audioContext: AudioContext;
  onClip: (seq: number, blob: Blob) => void;
  onError: (reason: ChatVoiceError) => void;
  onHintChange: (hint: ChatVoiceHint) => void;
}): VoiceCapture => {
  let stream: MediaStream | undefined;
  let analyser: LevelAnalyser | undefined;
  let clips: ClipRecorder | undefined;
  // onClip and onHintChange are plain DOM and rAF callbacks, not saga effects, so saga
  // cancellation does not stop them: stop() inside close() fires one last `dataavailable` AFTER
  // close was called. Without this guard a rep who cancels and immediately records again has the
  // old capture's trailing clip land in the new session's slots under a stale seq.
  let closed = false;

  // Read once for the whole session rather than per frame: see readVoiceTunables. A capture is
  // constructed per recording (store/chat/voice/sagas.ts), so pressing record again still picks up
  // an override changed in the URL meanwhile.
  const tunables = readVoiceTunables();

  let clipStartedAt = 0;
  let lastRms = 0;
  let speaking = false;
  let speakingSeen = false;
  let quietMs = 0;
  // pause() stops the recorder but does not stop the analyser's sampling loop, so onFrame keeps
  // running against the pre-pause clip's stale clipStartedAt. Without this flag a silence cut
  // fires mid-pause and reopens the microphone; the same gap exists between stop() and close().
  let recording = false;

  const guard =
    <A extends unknown[]>(handler: (...args: A) => void) =>
    (...args: A) => {
      if (!closed) {
        handler(...args);
      }
    };

  // Emits the clip only if speech occurred in it, then opens the next one on the same stream.
  // Cutting at SILENCE is what makes that swap free: the audio lost in the restart gap is silence,
  // which is also why we never have to capture raw PCM or hand-encode WAV.
  const cut = (reason: VoiceCutReason) => {
    const emit = speakingSeen;
    debugLogCut(reason, {
      clipMs: performance.now() - clipStartedAt,
      emit,
      quietMs,
      rms: lastRms,
    });
    speakingSeen = false;
    speaking = false;
    quietMs = 0;
    clipStartedAt = performance.now();
    void clips
      ?.flush(emit)
      .then(() => {
        // cut() is also reachable from onVisibilityChange while paused, so this continuation
        // needs its own recording check rather than relying on onFrame's early return.
        if (!closed && recording) {
          clips?.open();
        }
      })
      // Logged as well as reported: the saga treats any error event as Done, so a transient flush
      // rejection silently ends the recording — and UNKNOWN alone names no cause.
      .catch(
        guard((error: unknown) => {
          loggingService.error("Voice clip flush failed", error);
          onError(ChatVoiceError.UNKNOWN);
        })
      );
  };

  const onFrame = (rms: number, dtMs: number) => {
    if (!recording) {
      return;
    }
    debugRecordLevel(rms, dtMs);
    lastRms = rms;
    const nowSpeaking = isSpeech(rms, speaking, tunables);
    speaking = nowSpeaking;
    speakingSeen = speakingSeen || nowSpeaking;
    quietMs = isSilent(rms, tunables) ? quietMs + dtMs : 0;

    const clipMs = performance.now() - clipStartedAt;
    const pauseDone = speakingSeen && !nowSpeaking && quietMs >= tunables.SILENCE_MS;
    const longEnough = clipMs >= tunables.MIN_CLIP_MS;
    const tooLong = clipMs >= tunables.MAX_CLIP_MS;

    if (pauseDone && longEnough) {
      cut("silence");
    } else if (tooLong) {
      cut("maxClip");
    }
  };

  const onVisibilityChange = () => {
    // A backgrounded tab throttles timers to a second or worse, so the MAX_CLIP_MS guard in the
    // sampling loop cannot be trusted to fire anywhere near the cap there — the guard meant to
    // bound a backgrounded tab is the one least able to run in it. Hiding the tab ends the clip
    // instead.
    if (document.visibilityState === "hidden") {
      cut("hidden");
    }
  };

  const start = async () => {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // AGC continuously renormalises input level, which moves the ground under a fixed
          // RMS threshold. Deliberately off.
          autoGainControl: false,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (error) {
      // getUserMedia's await is the permission prompt, an unbounded user-paced wait. The rep can
      // cancel and close() this capture before answering it, so onError must stay behind the same
      // guard as every other callback here, or a late deny fires into whatever session is current.
      guard(() => onError(toVoiceError(error)))();
      throw error;
    }

    try {
      stream.getAudioTracks().forEach((track) => {
        track.onended = guard(() => onError(ChatVoiceError.STOPPED));
      });

      clips = createClipRecorder({ onClip: guard(onClip), stream });
      analyser = createLevelAnalyser({
        audioContext,
        onFrame: guard(onFrame),
        onHintChange: guard(onHintChange),
        stream,
        tunables,
      });

      document.addEventListener("visibilitychange", onVisibilityChange);
      clipStartedAt = performance.now();
      debugResetLevels();
      clips.open();
      recording = true;
    } catch (error) {
      // createClipRecorder falls back to an unguarded `new MediaRecorder` when the preferred MIME
      // type is unsupported, and that construction can itself throw on a browser that supports
      // neither path. Map it the same way as the getUserMedia failure above, or the rep is left
      // with a dead overlay and no explanation. Guarded for the same reason as the catch above.
      guard(() => onError(ChatVoiceError.UNKNOWN))();
      throw error;
    }
  };

  return {
    close: () => {
      if (closed) {
        return;
      }
      closed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      analyser?.stop();
      stream?.getTracks().forEach((track) => track.stop());
      // Swallowed, not reported: close() rejects with InvalidStateError when the context is
      // already closed — which the browser does on its own when it reclaims audio resources — and
      // that is the state close() was asking for anyway. Without the catch it surfaces as an
      // unhandled rejection in Sentry for a teardown that in fact succeeded. Nothing else in this
      // teardown depends on the result, so there is no failure left to handle.
      void audioContext.close().catch(() => {});
    },
    getLevel: () => analyser?.getLevel() ?? 0,
    pause: async () => {
      recording = false;
      await clips?.flush();
    },
    resume: () => {
      speaking = false;
      speakingSeen = false;
      quietMs = 0;
      clipStartedAt = performance.now();
      debugResetLevels();
      recording = true;
      clips?.open();
    },
    start,
    stop: async () => {
      recording = false;
      return clips ? clips.flush() : 0;
    },
  };
};

export default createVoiceCapture;
